import { List } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import Button from 'react-bootstrap/lib/Button';
import _ from 'lodash';

import { OrderSummaryContexts } from '../../constants/AppConstants';
import Order from '../../records/Order';
import OrderActions from '../../actions/OrderActions';
import { translate as t } from '../../lib/I18n';
import OrderTrip from '../../records/order/Trip';

import Icon from '../general/Icon';
import Price from '../general/Price';
import FareStations from './FareStations';
import RemoveJourneyModal from './RemoveJourneyModal';
import FareValidity from '../fares/FareValidity';
import AddFavouriteLink from '../fares/AddFavouriteLink';
import CreateAlert from '../disruptionAlert/CreateAlert';
import TicketCalculationsSummary from '../account/refund/TicketCalculationsSummary';
import LinkingSmartcardsStore from '../../stores/LinkingSmartcardsStore';
import connectToStores from '../../helpers/connectToStores';
import SmartcardStore from '../../stores/SmartcardStore';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import LinkedSmartcards from '../smartcard/LinkedSmartcards';
import { getFoldedTickets } from '../../helpers/Fares';
import RailcardBadge from '../fares/details/RailcardBadge';
import FeatureFlag from '../general/FeatureFlag';
import RewardPointsEarned from './orderSummary/RewardPointsEarned';

const getStateFromStores = ({ orderTrip }) => {
    return {
        enableSmartcardAssociations: FeatureFlagStore.get('enableSmartcardAssociations'),
        linkedAdults: LinkingSmartcardsStore.getAdultsLinkedSmartcardsForFare(orderTrip.getSeasonFare().tickets.map(ticket => ticket.id)),
        linkedChildren: LinkingSmartcardsStore.getChildrenLinkedSmartcardsForFare(orderTrip.getSeasonFare().tickets.map(ticket => ticket.id)),
    };
};

class SeasonTrip extends React.PureComponent {

    static propTypes = {
        index: PropTypes.number.isRequired,
        order: PropTypes.instanceOf(Order).isRequired,
        refundDetails: PropTypes.instanceOf(Order),
        storedRefundCalculations: PropTypes.instanceOf(List),
        orderTrip: PropTypes.instanceOf(OrderTrip).isRequired,
        open: PropTypes.bool,
        isLoggedIn: PropTypes.bool,
        isFareOnly: PropTypes.bool,
        linkedAdults: PropTypes.instanceOf(List),
        linkedChildren: PropTypes.instanceOf(List),
        enableSmartcardAssociations: PropTypes.bool,
        context: PropTypes.oneOf(_.values(OrderSummaryContexts)),
        setBasketChanged: PropTypes.func,
    };

    static defaultProps = {
        open: false,
        isLoggedIn: false,
        isFareOnly: false,
        context: null,
        setBasketChanged: _.noop,
    };

    state = {
        open: this.props.open,
        journeyToRemove: null
    };

    _handleRemoveJourney = (journey) => () => {
        this.setState({ journeyToRemove: journey });
    };

    _handleRemoveJourneyBack = () => {
        this.setState({ journeyToRemove: null });
    };

    _handleRemoveJourneyConfirm = () => {
        OrderActions.deleteTrip(this.props.order, this.props.orderTrip);
        this.setState({ journeyToRemove: null });
    };

    _renderRemoveConfirm() {
        if (this.state.journeyToRemove) {
            return (
                <RemoveJourneyModal
                    journey={this.state.journeyToRemove}
                    onBack={this._handleRemoveJourneyBack}
                    onConfirm={() => {
                        this._handleRemoveJourneyConfirm();
                        this.props.setBasketChanged();
                    }}
                />
            );
        }

        return null;
    }

    _renderRemoveJourney() {
        if (!this.props.order.isPaid()) {
            return (
                <Button className="journey-remove hidden-print btn-plain pull-right" onClick={this._handleRemoveJourney(this.props.orderTrip)}>
                    <span className="sr-only">{t('order.summary.removeJourney')}</span>
                    <Icon icon="bin" />
                </Button>
            );
        }

        return null;
    }

    renderFavourite() {
        const fare = this.getFare();
        const {
            orderTrip,
            order: {
                delivery
            }
        } = this.props;

        if (fare && !fare.isExcludedFromFavourites()) {
            return (
                <AddFavouriteLink
                    fare={fare}
                    orderTrip={orderTrip}
                    delivery={delivery}
                    context={fare.isFlexi() ? "flexi-checkout" : "season-checkout"}
                />
            );
        }

        return null;
    }

    renderCreateAlert() {
        const { order, orderTrip } = this.props;

        return (
            <CreateAlert
                order={order}
                orderTrip={orderTrip}
                context="point-checkout"
            />
        );
    }

    renderTripActions() {
        const { isLoggedIn } = this.props;

        return (
            <Row>
                <Col xs={12} className="fare-actions pull-right">
                    {isLoggedIn && this.renderFavourite()}
                    {isLoggedIn && this.props.open && this.renderCreateAlert()}
                </Col>
            </Row>
        );
    }

    getCalculation(ticket) {
        const { storedRefundCalculations } = this.props;

        if (!ticket || !storedRefundCalculations || storedRefundCalculations.size === 0) {
            return null;
        }

        return storedRefundCalculations.tickets.find(c =>
            c.getIn(['calculation', 'ticketId']) === ticket.id
            && c.getIn(['calculation', 'refundAmount']) > 0);
    }

    renderRefundCalculations(orderFare, tripTitle) {
        const { refundDetails, order, context } = this.props;
        const RefundTitleTag = context === OrderSummaryContexts.BASKET_MODAL ? 'h3' : 'h4';

        return (
            <div className="margin-top border--top">
                <RefundTitleTag className="margin-top--sm margin-bottom--sm text-weight--bold">{t('account.orders.refund.tickets')}</RefundTitleTag>
                {orderFare.tickets.map((ticket, index) =>
                    <TicketCalculationsSummary
                        key={index}
                        refundDetails={refundDetails}
                        storedRefundCalculation={this.getCalculation(ticket)}
                        ticket={ticket}
                        shouldUseCalculations={_.get(ticket, 'railcard.code') !== 'TSU'}
                        tripTitle={tripTitle}
                        isSmartcard={order.isSmartcard()}
                    />
                )}
            </div>
        );
    }

    getFare() {
        const { isFareOnly, orderTrip } = this.props;
        return isFareOnly ? orderTrip.getFareOnlyFare() : orderTrip.getSeasonFare();
    }

    render() {
        const { orderTrip, index, order, enableSmartcardAssociations, linkedAdults, linkedChildren, context } = this.props;

        const titleKey = orderTrip.isSmartcard() ? 'smartcard_title' : 'journey_title';
        const tripTitle = t(`order.summary.${titleKey}`, { number: index + 1 });
        const TripTitleTag = context === OrderSummaryContexts.BASKET_MODAL ? 'h2' : 'h3';

        const orderFare = this.getFare();
        const tickets = getFoldedTickets(orderFare.tickets);
        const rewardPoints = orderFare.tickets.reduce((sum, ticket) => sum + ticket.rewardPoints, 0);

        let activeRailcardCode = null;

        if (orderTrip.hasJCPRailcard()) {
            activeRailcardCode = 'JCP';
        } else if (orderTrip.has1617Railcard()) {
            activeRailcardCode = 'TSU';
        }

        let railcardName = t(`booking.railcard.${activeRailcardCode}`);

        return (
            <li className="order-summary__item margin-bottom order-summary__item--expanded">
                {this._renderRemoveConfirm()}

                <div className="box-headed box-headed--primary">
                    <div className="box-headed__header box--padded text-weight--normal">
                        <Row>
                            <Col xs={8}>
                                <TripTitleTag className="journey-heading text-weight--normal">
                                    {tripTitle}
                                </TripTitleTag>
                            </Col>

                            <Col xs={4} className="text-right price">
                                <Price price={orderTrip.getTotalPrice()} />
                            </Col>
                        </Row>
                    </div>

                    <div className="box-headed__body box--padded">
                        <Row className="fare-summary">
                            <Col xs={12} md={6}>
                                <FareStations
                                    origin={orderFare.origin}
                                    destination={orderFare.destination}
                                />
                            </Col>

                            <Col xs={12} md={6} lg={6} className="text-weight--normal ticket-type">
                                {orderFare.getName()}
                            </Col>
                        </Row>

                        {orderFare.tickets.every(ticket => ticket.isRefunded) &&
                            <Row>
                                <Col xs={12} md={6} className="refunded pull-right">
                                    <span className="alert-success text-light pull-right">
                                        {t('account.orders.refund.ticket.refunded')}
                                    </span>
                                </Col>
                            </Row>
                        }

                        <Row>
                            {activeRailcardCode && <Col xs={12} sm={10}>
                                <p className="text-primary text-sm"> {t(`account.orders.railcards`, { railcardName })}</p>
                            </Col>}

                            <Col xs={12} sm={8}>
                                <FareValidity
                                    from={orderFare.outwardJourney.validFrom}
                                    to={orderFare.outwardJourney.validTo}
                                />
                            </Col>

                            {(!enableSmartcardAssociations && tickets.first().railcard) &&
                                <Col xs={12} sm={4}>
                                    <div className="pull-right">
                                        <RailcardBadge railcard={tickets.first().railcard} />
                                    </div>
                                </Col>
                            }

                            <Col xs={12} sm={4}>
                                {this._renderRemoveJourney()}
                            </Col>
                        </Row>

                        {enableSmartcardAssociations && tickets.first().adults > 0 &&
                            <Row>
                                <Col xs={12} className='order-summary__item'>
                                    <LinkedSmartcards linkedPassengers={linkedAdults} tickets={tickets} showRailcardsMessage />
                                </Col>
                            </Row>
                        }

                        {enableSmartcardAssociations && tickets.first().children > 0 &&
                            <Row>
                                <Col xs={12} className='order-summary__item'>
                                    <LinkedSmartcards linkedPassengers={linkedChildren} tickets={tickets} isChild showRailcardsMessage />
                                </Col>
                            </Row>
                        }
                        <FeatureFlag name="enableRewardPoints">
                            <RewardPointsEarned rewardPoints={rewardPoints} className="reward-points__earned pull-right" />
                        </FeatureFlag>
                        {this.renderTripActions()}
                        {order.hasRefunds() && orderFare.tickets.some(ticket => ticket.isRefunded) && this.renderRefundCalculations(orderFare, tripTitle)}
                    </div>
                </div>
            </li>
        );
    }
}

export default connectToStores(
    [FeatureFlagStore, SmartcardStore, LinkingSmartcardsStore],
    getStateFromStores,
)(SeasonTrip);
